import { DEMO_MODE_ACTIVE, OFFLINE_MODE_ACTIVE, getAuthHeaders, isPresignedUrlReachable, runBulkWithSimulatedProgress } from "../utils/servicesUtils";
import { apiFetch } from "../utils/apiFetch";
import { startLiveProgressListener } from "./webSocketServices";

// Controlled access to obligation hierarchy based on user permissions
export const getObligationStructures = async ({ signal } = {}) => {
  const ENDPOINT = "/api/obligation/structures/lite/";

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
    signal,
  });

  if (!response.ok) throw new Error("Failed to load records");
  return await response.json();
};

// Controlled access to obligation hierarchy based on verification flag and user permissions
export const getObligationVerificationStructures = async ({ signal } = {}) => {
  const ENDPOINT = "/api/obligation/verificator/structures/lite/";

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
    signal,
  });

  if (!response.ok) throw new Error("Failed to load records");
  return await response.json();
};

export const getMyObligationsByFramework = async (framework_id) => {
  const ENDPOINT = `/api/obligation/my/groups/by-framework/${encodeURIComponent(framework_id)}`;

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to load records");
  return await response.json();
};

export const getMyRecurrencesByObligation = async (group_id) => {
  const ENDPOINT = `/api/obligation/my/obligations/by-group/${encodeURIComponent(group_id)}`;

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to load records");
  return await response.json();
};

export const getObligationGroupDetails = async (group_id) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(group_id)}/details/`;

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to get details");
  return await response.json();
};

export const getObligationGroupDetailsForVerificator = async (group_id) => {
  const ENDPOINT = `/api/obligation/verificator/${encodeURIComponent(group_id)}/details/`;

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to get details");
  return await response.json();
};

/**
 * Lightweight obligation/group details for headers, sidebars, and batched hydration.
 *
 * @param {string} group_id - Obligation group id (path segment).
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - Optional. Files header hydration passes this through its
 *   scheduler so scrolled-away cards can cancel both queued and in-flight lite requests.
 * @returns {Promise<object>} Parsed JSON body.
 */
export const getObligationGroupDetailsLite = async (group_id, { signal } = {}) => {
  const ENDPOINT = `/api/obligation/lite/${encodeURIComponent(group_id)}/`;

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
    signal,
  });

  if (!response.ok) throw new Error("Failed to get details");
  return await response.json();
};

export const getObligationRecurrenceDetails = async (obligation_id) => {
  const ENDPOINT = `/api/obligation/deep/${encodeURIComponent(obligation_id)}/details/`;

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) throw new Error("Failed to get details");
  return await response.json();
};

// expected body structure:
// {
//   "group_ids": [
//      "id1",
//      "id2",
//   ]
// }
export const getObligationGroupsStatusCounts = async (body) => {
  const ENDPOINT = "/api/obligation/groups/status-counts/";

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while retrieving group status counts");
    }
    throw error;
  }
};

// expected body structure:
// {
//   "group_ids": [
//      "id1",
//      "id2",
//   ]
// }
export const getObligationGroupsVerificationStatusCounts = async (body) => {
  const ENDPOINT = "/api/obligation/verificator/groups/status-counts/";

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while retrieving verification group status counts");
    }
    throw error;
  }
};

// expected body structure:
// {
//   "group_ids": [
//      "id1",
//      "id2",
//   ]
// }
export const getAssignableVerificationUsers = async () => {
  const ENDPOINT = "/api/obligation/verificator/list/";

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while retrieving assignable verification users");
    }
    throw error;
  }
};

export const getObligationsPendingVerificationCount = async () => {
  const ENDPOINT = "/api/obligation/verificator/pending-verification/count/";

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while retrieving pending verification obligations count");
    }
    throw error;
  }
};

// expected body structure:
// {
//   "user_id": "5eb7cf5a86d9755df3a6c593",
//   "department_id": "5eb7cf5a86d9755df3a6c593",
//   "start": "2026-01-21T21:22:05.064Z",
//   "end": "2026-01-21T21:22:05.064Z",
//   "scope": "single"
// }
export const reassignObligationRecurrence = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/reassign/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while reassigning the obligation");
    }
    throw error;
  }
};

export const removeSecondaryUsersFromObligationRecurrence = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/secondary-responsibles/remove/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while updating the secondary responsible users");
    }
    throw error;
  }
};

// expected body structure:
// {
//   "scope": "single" | "group",
//   "new_responsible_id": "5eb7cf5a86d9755df3a6c593"
// }
export const assignVerificationResponsible = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/verification/responsible/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while reassigning the verification responsible");
    }
    throw error;
  }
};

// expected body structure:
// {
//   "notes": [
//     "string"
//   ]
// }
export const postObligationRecurrenceNotes = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/notes/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while posting obligation notes");
    }
    throw error;
  }
};

export const deleteObligationRecurrenceNote = async (obligation_id, note_id) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/notes/${encodeURIComponent(note_id)}/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while deleting obligation note");
    }
    throw error;
  }
};

export const verifyObligationRecurrenceCriteria = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/verify/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while verifying obligation criteria");
    }
    throw error;
  }
};

/* ================================ REFERENCE MANAGEMENT ================================= */
export const downloadObligationReferenceDocument = async (document_id) => {
  const ENDPOINT = `/api/obligation/references/documents/${encodeURIComponent(document_id)}/download/`;

  if (OFFLINE_MODE_ACTIVE) {
    return {
      document_id,
      download_url: `/file_.pdf?ts=${Date.now()}`,
      mime_type: "application/pdf",
      error: null
    };
  }

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders()
  }, { skipAuthHandling: true });

  if (!response.ok) {
    return {
      document_id,
      download_url: DEMO_MODE_ACTIVE ? `/file_.pdf?ts=${Date.now()}` : null,
      mime_type: DEMO_MODE_ACTIVE ? "application/pdf" : null,
      error: DEMO_MODE_ACTIVE ? null : new Error("Failed to download file")
    };
  }

  const content = await response.json();

  // Clean and extract URL
  const rawUrl = content?.download_url || null;
  const cleanUrl = rawUrl ? rawUrl.replace(/(^"|"$)/g, "") : null;
  const isUrlReachable = cleanUrl ? await isPresignedUrlReachable(cleanUrl) : false;

  if (!isUrlReachable) {
    return {
      document_id: content.document_id,
      download_url: DEMO_MODE_ACTIVE ? `/file_.pdf?ts=${Date.now()}` : null,
      mime_type: DEMO_MODE_ACTIVE ? "application/pdf" : null,
      error: DEMO_MODE_ACTIVE ? null : new Error("File URL is unreachable or invalid")
    };
  }

  return {
    document_id: content.document_id,
    download_url: cleanUrl,
    mime_type: content?.mime_type || null,
    error: null
  };
};

/**
 * Upload reference files for a single obligation
 * @param {string} group_id - Obligation group ID
 * @param {File[]} files - Files to upload
 * @param {Object} tags - Optional tags
 * @returns {Promise} API response
 */
export const uploadReferenceFiles = async (group_id, files, tags = null) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(group_id)}/references/documents/upload/`;
  const headers = getAuthHeaders();

  // For formData, remove Content-Type to let the browser set the correct boundary.
  delete headers["Content-Type"];

  // Multipart payload expected by the server
  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  if (tags) formData.append("tags", JSON.stringify(tags));

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers,
      body: formData,
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while posting reference files");
    }

    throw error;
  }
};

export const deleteReferenceFiles = async (group_id, reference_id) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(group_id)}/references/documents/${encodeURIComponent(reference_id)}/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while deleting reference files");
    }

    throw error;
  }
};

/**
 * Post reference text for a single obligation
 * @param {string} group_id - Obligation group ID
 * @param {Object} body - POST body object, expects
 * {
 *    "text": "string"
 * }
 * @returns {Promise} API response
 */
export const postReferenceText = async (group_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(group_id)}/references/text/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while posting reference text");
    }
    throw error;
  }
};

export const deleteReferenceText = async (group_id, reference_id) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(group_id)}/references/text/${encodeURIComponent(reference_id)}/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while deleting reference files");
    }

    throw error;
  }
};

/* ================================= EVIDENCE MANAGEMENT ================================= */
/**
 * Returns presigned download URL + MIME for a recurrence evidence document (GET; JSON body).
 * Used by Files V2 `FilePreview`, obligation file widgets (`useFileData`), etc.
 *
 * @param {string} evidence_id - Evidence document id.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - Optional. Pass from `FilePreview` / `AbortController` to cancel
 *   when the preview card unmounts or the user scrolls away — frees browser connections and avoids work
 *   for off-screen cards. Existing callers that omit `options` behave unchanged (`signal` is undefined).
 * @returns {Promise<{ evidence_id?: string, download_url: string|null, mime_type: string|null, error: Error|null }>}
 */
export const downloadRecurrenceEvidenceDocument = async (evidence_id, { signal } = {}) => {
  const ENDPOINT = `/api/obligation/evidence/${encodeURIComponent(evidence_id)}/download/`;

  if (OFFLINE_MODE_ACTIVE) {
    return {
      evidence_id,
      download_url: `/file_.pdf?ts=${Date.now()}`,
      mime_type: "application/pdf",
      error: null
    };
  }

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
    signal,
  }, { skipAuthHandling: true });

  if (!response.ok) {
    return {
      evidence_id,
      download_url: DEMO_MODE_ACTIVE ? `/file_.pdf?ts=${Date.now()}` : null,
      mime_type: DEMO_MODE_ACTIVE ? "application/pdf" : null,
      error: DEMO_MODE_ACTIVE ? null : new Error("Failed to download file")
    };
  }

  const content = await response.json();

  // Clean and extract URL
  const rawUrl = content?.download_url || null;
  const cleanUrl = rawUrl ? rawUrl.replace(/(^"|"$)/g, "") : null;
  const isUrlReachable = cleanUrl ? await isPresignedUrlReachable(cleanUrl) : false;

  if (!isUrlReachable) {
    return {
      evidence_id: content.document_id,
      download_url: DEMO_MODE_ACTIVE ? `/file_.pdf?ts=${Date.now()}` : null,
      mime_type: DEMO_MODE_ACTIVE ? "application/pdf" : null,
      error: DEMO_MODE_ACTIVE ? null : new Error("File URL is unreachable or invalid")
    };
  }

  return {
    document_id: content.document_id,
    download_url: cleanUrl,
    mime_type: content?.mime_type || null,
    error: null
  };
};

/**
 * Get presigned URL for a criteria PDF by obj_key.
 * Uses GET /api/obligation/pdf/criteria/presigned-url/?obj_key=...
 * Used by Files V2 preview when the file list has criteria_pdf with obj_key from POST /api/files/.
 *
 * @param {string} obj_key - Storage object key for the criteria PDF.
 * @param {object} [options]
 * @param {AbortSignal} [options.signal] - Optional. Pass from `FilePreview` to cancel in-flight request on
 *   unmount/scroll. Omitted by legacy callers — behavior unchanged.
 * @returns {Promise<{ download_url: string|null, mime_type: string, error: Error|null }>}
 */
export const getCriteriaPdfPresignedUrl = async (obj_key, { signal } = {}) => {
  if (!obj_key) {
    return { download_url: null, mime_type: "application/pdf", error: new Error("obj_key is required") };
  }
  const ENDPOINT = `/api/obligation/pdf/criteria/presigned-url/?obj_key=${encodeURIComponent(obj_key)}`;

  if (OFFLINE_MODE_ACTIVE) {
    return {
      download_url: `/file_.pdf?ts=${Date.now()}`,
      mime_type: "application/pdf",
      error: null
    };
  }

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders(),
    signal,
  }, { skipAuthHandling: true });

  if (!response.ok) {
    return {
      download_url: DEMO_MODE_ACTIVE ? `/file_.pdf?ts=${Date.now()}` : null,
      mime_type: DEMO_MODE_ACTIVE ? "application/pdf" : null,
      error: DEMO_MODE_ACTIVE ? null : new Error("Failed to get criteria PDF URL")
    };
  }

  const content = await response.json();
  const rawUrl = content?.download_url || null;
  const cleanUrl = rawUrl ? rawUrl.replace(/(^"|"$)/g, "") : null;
  const isUrlReachable = cleanUrl ? await isPresignedUrlReachable(cleanUrl, signal) : false;

  if (!isUrlReachable) {
    return {
      download_url: DEMO_MODE_ACTIVE ? `/file_.pdf?ts=${Date.now()}` : null,
      mime_type: DEMO_MODE_ACTIVE ? "application/pdf" : null,
      error: DEMO_MODE_ACTIVE ? null : new Error("File URL is unreachable or invalid")
    };
  }

  return {
    download_url: cleanUrl,
    mime_type: content?.mime_type || "application/pdf",
    error: null
  };
};

export const downloadCriteriaPdfDocument = async (obligation_id) => {
  const ENDPOINT = `/api/obligation/pdf/criteria/${encodeURIComponent(obligation_id)}/`;

  if (OFFLINE_MODE_ACTIVE) {
    return {
      obligation_id,
      download_url: `/file_.pdf?ts=${Date.now()}`,
      mime_type: "application/pdf",
      error: null
    };
  }

  const response = await apiFetch(ENDPOINT, {
    method: "GET",
    headers: getAuthHeaders()
  }, { skipAuthHandling: true });

  if (!response.ok) {
    return {
      obligation_id,
      download_url: DEMO_MODE_ACTIVE ? `/file_.pdf?ts=${Date.now()}` : null,
      mime_type: DEMO_MODE_ACTIVE ? "application/pdf" : null,
      error: DEMO_MODE_ACTIVE ? null : new Error("Failed to download criteria pdf")
    };
  }

  const content = await response.json();

  // Clean and extract URL
  const rawUrl = content?.download_url || null;
  const cleanUrl = rawUrl ? rawUrl.replace(/(^"|"$)/g, "") : null;
  const isUrlReachable = cleanUrl ? await isPresignedUrlReachable(cleanUrl) : false;

  if (!isUrlReachable) {
    return {
      obligation_id,
      download_url: DEMO_MODE_ACTIVE ? `/file_.pdf?ts=${Date.now()}` : null,
      mime_type: DEMO_MODE_ACTIVE ? "application/pdf" : null,
      error: DEMO_MODE_ACTIVE ? null : new Error("File URL is unreachable or invalid")
    };
  }

  return {
    obligation_id,
    download_url: cleanUrl,
    mime_type: content?.mime_type || null,
    error: null
  };
};

// expected assignments structure:
// [
//   {
//     "file_index": 0,
//     "criterion_ids": ["345137092ebb43d8bbe046641509e4ca"]
//   }
// ]
export const uploadEvidenceFiles = async (obligation_id, files, assignments) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/evidence/files/`;
  const headers = getAuthHeaders();

  // For formData, remove Content-Type to let the browser set the correct boundary.
  delete headers["Content-Type"];

  // Multipart payload expected by the server
  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  formData.append("assignments", JSON.stringify(assignments));

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers,
      body: formData,
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while posting evidence files");
    }

    throw error;
  }
};

// expected body structure:
// {
//   "criterion_ids": [
//     "string"
//   ],
//   "delete_if_orphan": false
// }
export const deleteEvidenceFiles = async (obligation_id, evidence_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/evidence/${encodeURIComponent(evidence_id)}/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "DELETE",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while deleting evidence files");
    }

    throw error;
  }
};

// expected body structure:
// [
//   {
//     "content": "declarative",
//     "criterion_ids": [
//       "345137092ebb43d8bbe046641509e4ca"
//     ]
//   }
// ]
export const addEvidenceDeclarative = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/declarative/${encodeURIComponent(obligation_id)}/criteria/batch/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while adding evidence comments");
    }

    throw error;
  }
};

// expected body structure:
// {
//   "criterion_ids": [
//     "string"
//   ],
// }
export const deleteEvidenceDeclarative = async (obligation_id, declarative_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/declarative/${encodeURIComponent(declarative_id)}/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "DELETE",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while deleting evidence declarative");
    }

    throw error;
  }
};

// expected body structure:
// [
//   {
//     "content": "link",
//     "criterion_ids": [
//       "345137092ebb43d8bbe046641509e4ca"
//     ]
//   }
// ]
export const addExternalLinks = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/comments/batch/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while adding evidence comments");
    }

    throw error;
  }
};

// expected body structure:
// {
//   "criterion_ids": [
//     "string"
//   ],
//   "delete_if_orphan": false
// }
export const deleteExternalLinks = async (obligation_id, comment_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/comments/${encodeURIComponent(comment_id)}/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "DELETE",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while deleting evidence comments");
    }

    throw error;
  }
};

// expected body structure:
// {
//   "assignments": [
//     {
//       "criterion_ids": [
//         "345137092ebb43d8bbe046641509e4ca"
//       ],
//       "obligation_ids": [
//         "696541c9093c2467d6ef95a7"
//       ]
//     }
//   ]
// }
export const linkObligationToCriteria = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/link-obligations/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while linking obligations as evidence");
    }

    throw error;
  }
};

// expected body structure:
// {
//   "criterion_ids": [
//     "string"
//   ]
// }
export const removeObligationLinkFromCriteria = async (obligation_id, linked_obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/${encodeURIComponent(linked_obligation_id)}/unlink/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "DELETE",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while removing obligation evidence links");
    }

    throw error;
  }
};

// expected body structure:
// {
//   "evidence_type": "evidence" | "comment" | "declarative",
//   "target_id": "string" - id of the evidence that is being checked
//   "criterion_ids": [
//     "string"
//   ]
// }
export const getEvidenceDeletionPermissions = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/evidence/can-delete/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while retrieving the deletion permissions for the evidence");
    }

    throw error;
  }
};

// expected body structure (use formdata):
// {
//   "criteria_verification_ids": [
//     "string"
//   ],
//   "files": [file1, file2]
// }
export const uploadVerificationCriteriaEvidenceFiles = async (obligation_id, files, criteria_verification_ids) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/verification/items/evidence/`;
  const headers = getAuthHeaders();

  // For formData, remove Content-Type to let the browser set the correct boundary.
  delete headers["Content-Type"];

  // Multipart payload expected by the server
  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  criteria_verification_ids.forEach((id) => {
    formData.append("criteria_verification_ids", id);
  });

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers,
      body: formData,
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while posting evidence files");
    }

    throw error;
  }
};

// expected body structure (use formdata):
// {
//   "criteria_verification_ids": [
//     "string"
//   ],
//   "declarative_content": "string" | null,
//   "comment": "string" | null
// }
export const uploadVerificationCriteriaEvidenceGeneral = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/verification/items/content/`;
  const headers = getAuthHeaders();

  // For formData, remove Content-Type to let the browser set the correct boundary.
  delete headers["Content-Type"];

  const formData = new FormData();

  body.criteria_verification_ids.forEach((id) => {
    formData.append("criteria_verification_ids", id);
  });

  if (body.declarative_content != null) formData.append("declarative_content", body.declarative_content);
  if (body.comment != null) formData.append("comment", body.comment);

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers,
      body: formData,
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while adding evidence comments");
    }

    throw error;
  }
};

// expected body structure:
// {
//   "evidence_ids": [
//     "string"
//   ],
//   "declarative_ids": [
//     "string"
//   ],
//   "comment_ids": [
//     "string"
//   ]
// }
export const deleteVerificationCriteriaEvidence = async (obligation_id, criteria_verification_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/${encodeURIComponent(criteria_verification_id)}/content/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "DELETE",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while deleting the evidence");
    }

    throw error;
  }
};

// expected body structure:
// {
//   "evidence_type": "evidence" | "comment" | "declarative",
//   "target_id": "string" - id of the evidence that is being checked
//   "criterion_ids": [
//     "string"
//   ]
// }
export const getVerificationEvidenceDeletionPermissions = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/verification/item/evidence/can-delete/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while retrieving the deletion permissions for the evidence");
    }

    throw error;
  }
};

/* ================================ RECURRENCE MANAGEMENT ================================ */
// expected body structure:
// {
//   "completed_date": "2026-01-15T22:48:37.537Z"
// }
export const completeRecurrence = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/complete/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while completing the recurrence instance");
    }

    throw error;
  }
};

export const undoRecurrenceCompletion = async (obligation_id) => {
  const ENDPOINT = `/api/obligation/reopen/${encodeURIComponent(obligation_id)}/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "PATCH",
      headers: getAuthHeaders(),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while reopening the recurrence instance");
    }

    throw error;
  }
};

export const completeVerificationCriteria = async (obligation_id) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/verification/items/verify/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "PATCH",
      headers: getAuthHeaders()
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while completing the verification of the recurrence");
    }

    throw error;
  }
};

// expected body structure:
// {
//   "criteria_verification_ids": [
//     "string"
//   ]
// }
export const undoVerificationCriteriaCompletion = async (obligation_id, body) => {
  const ENDPOINT = `/api/obligation/${encodeURIComponent(obligation_id)}/criteria/verification/items/reset/`;

  try {
    const response = await apiFetch(ENDPOINT, {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw responseData;
    }

    return responseData;
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while undoing the completion of the verification of the recurrence");
    }

    throw error;
  }
};

/* ============================ BULK SERVICES FOR OBLIGATIONS ============================ */
export const bulkAssignResponsible = async (body = {}, onProgress = () => { }) => {
  const ENDPOINT = "/api/obligation/bulk-assign";
  const request = {
    url: ENDPOINT,
    options: {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }
  };

  const result = await startLiveProgressListener(request, onProgress);
  return result;
};

/**
 * Simulates progress updates for bulk assignment
 * This is a temporary solution until websocket/SSE is implemented
 */
export const bulkAssignResponsibleV2 = async (body = {}, onProgress = () => { }) => {
  return runBulkWithSimulatedProgress({
    endpoint: "/api/obligation/bulk-assign",
    fetchOptions: {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    },
    itemCount: body.group_ids?.length,
    onProgress,
    errorLabel: "Bulk assign responsible error",
  });
};

/**
 * Upload reference files to multiple obligations in bulk with progress tracking
 * @param {Array} files - Array of File objects
 * @param {Object} payload - Object containing group_ids as an array, it has to be defined as:
 * {
 *    group_ids: ["string", "string"]
 * }
 * @param {string} tags - Tags of the files
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Summary of results
 */
export const bulkUploadReferenceFiles = async (
  files = [],
  payload,
  tags,
  onProgress = () => { },
) => {
  const ENDPOINT = "/api/obligation/upload/documents/bulk/reference/";
  const headers = getAuthHeaders();

  // For formData, remove Content-Type to let the browser set the correct boundary.
  delete headers["Content-Type"];

  // Multipart payload expected by the server
  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  formData.append("payload", JSON.stringify(payload));

  if (tags) formData.append("tags", JSON.stringify(tags));

  const request = {
    url: ENDPOINT,
    options: {
      method: "POST",
      headers,
      body: formData,
    }
  };

  const result = await startLiveProgressListener(request, onProgress);
  return result;
};

export const bulkUploadReferenceFilesV2 = async (
  files = [],
  payload,
  tags,
  onProgress = () => { },
) => {
  const headers = getAuthHeaders();

  // For formData, remove Content-Type to let the browser set the correct boundary.
  delete headers["Content-Type"];

  // Multipart payload expected by the server
  const formData = new FormData();

  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  formData.append("payload", JSON.stringify(payload));

  if (tags) formData.append("tags", JSON.stringify(tags));

  return runBulkWithSimulatedProgress({
    endpoint: "/api/obligation/upload/documents/bulk/reference/",
    fetchOptions: {
      method: "POST",
      headers,
      body: formData,
    },
    itemCount: payload.group_ids?.length,
    onProgress,
    errorLabel: "Bulk upload reference files error",
  });
};

/**
 * Post reference text to multiple obligations in bulk with progress tracking
 * @param {Object} body - Object with group IDs and reference text
 *
  expected body:
  {
    "group_ids": [
      "string"
    ],
    "text": "string"
  }
*
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Summary of results
 */
export const bulkPostReferenceText = async (
  body = {},
  onProgress = () => { },
) => {
  const ENDPOINT = "/api/obligation/references/text/bulk/";
  const request = {
    url: ENDPOINT,
    options: {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }
  };

  const result = await startLiveProgressListener(request, onProgress);
  return result;
};

export const bulkPostReferenceTextV2 = async (
  payload,
  onProgress = () => { },
) => {
  const itemCount = payload.group_ids?.length || 1;
  const textLength = payload.text?.length || 0;

  // Base: 100ms per item, +50ms per 100 characters
  const baseTime = itemCount * 100;
  const textTime = (textLength / 100) * 50;
  const estimatedDuration = Math.min(Math.max(baseTime + textTime, 2000), 10000);

  return runBulkWithSimulatedProgress({
    endpoint: "/api/obligation/references/text/bulk/",
    fetchOptions: {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    },
    estimatedDuration,
    onProgress,
    errorLabel: "Bulk post reference text error",
  });
};

/**
 * Post reference text to multiple obligations in bulk with progress tracking
 * @param {Object} body - Object with group IDs and reference text
 *
  expected body:
  {
    "group_ids": [
      "string"
    ],
    "kind": "string" - text | document
  }
*
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Summary of results
 */
export const bulkDeleteReferences = async (
  body,
  onProgress = () => { },
) => {
  const ENDPOINT = "/api/obligation/references/bulk/delete/";
  const request = {
    url: ENDPOINT,
    options: {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }
  };

  const result = await startLiveProgressListener(request, onProgress);
  return result;
};

export const bulkDeleteReferencesV2 = async (
  payload,
  onProgress = () => { },
) => {
  return runBulkWithSimulatedProgress({
    endpoint: "/api/obligation/references/bulk/delete/",
    fetchOptions: {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    },
    itemCount: payload.group_ids?.length,
    onProgress,
    errorLabel: "Bulk delete references error",
  });
};

/**
 * Enable verification requirement for multiple obligations in bulk with configurable specifications
 * @param {Object} body - Object with the enable config
 *
  expected body:
  {
    "group_ids": [
      "5eb7cf5a86d9755df3a6c593"
    ],
    "verify_all_existing": true,
    "start_date_verification": "2026-06-18T15:33:03.887Z",
    "verification_responsible": [
      "5eb7cf5a86d9755df3a6c593"
    ],
    "verify_existing_completed": true,
    "verification_flow": "standard",
    "criteria_verification_enabled": true,
    "criteria_verification_items": [
      {
        "criterion_tag": "string",
        "criterion_name": "string",
        "description": "string"
      }
    ]
  }
*
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Summary of results
 */
export const enableObligationGroupsVerificationRequirement = async (body = {}, onProgress = () => { }) => {
  const ENDPOINT = "/api/obligation/verification/enable/";
  const request = {
    url: ENDPOINT,
    options: {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }
  };

  const result = await startLiveProgressListener(request, onProgress);
  return result;
};

/**
 * Simulates progress updates for enabling bulk verification requirement
 * This is a temporary solution until websocket/SSE is implemented
 */
export const enableObligationGroupsVerificationRequirementV2 = async (body = {}, onProgress = () => { }) => {
  return runBulkWithSimulatedProgress({
    endpoint: "/api/obligation/verification/enable/",
    fetchOptions: {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    },
    itemCount: body.group_ids?.length,
    onProgress,
    errorLabel: "Bulk change verification requirement error",
  });
};

/**
 * Disable verification requirement for multiple obligations in bulk
 * @param {Object} body - Object with the disable config
 *
  expected body:
  {
    "group_ids": [
      "5eb7cf5a86d9755df3a6c593"
    ]
  }
*
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} Summary of results
 */
export const disableObligationGroupsVerificationRequirement = async (body = {}, onProgress = () => { }) => {
  const ENDPOINT = "/api/obligation/verification/disable/";
  const request = {
    url: ENDPOINT,
    options: {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    }
  };

  const result = await startLiveProgressListener(request, onProgress);
  return result;
};

/**
 * Simulates progress updates for disabling bulk verification requirement
 * This is a temporary solution until websocket/SSE is implemented
 */
export const disableObligationGroupsVerificationRequirementV2 = async (body = {}, onProgress = () => { }) => {
  return runBulkWithSimulatedProgress({
    endpoint: "/api/obligation/verification/disable/",
    fetchOptions: {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    },
    itemCount: body.group_ids?.length,
    onProgress,
    errorLabel: "Bulk change verification requirement error",
  });
};

export const getAiObligationAnswer = async (payload) => {
  // Offline behavior:
  // when REACT_APP_MODE_OFFLINE is "true", skip the LLM call and return a generic message.
  const shouldSkipAICall = process.env.REACT_APP_MODE_OFFLINE === "true";
  if (shouldSkipAICall) {
    const language = (localStorage.getItem("i18nextLng") || "en").toLowerCase();
    const isSpanish = language.startsWith("es");

    if (isSpanish) {
      return {
        response: "Puede revisar los anexos de esta obligacion para obtener mas informacion, recuerde revisar la documentacion oficial, los archivos cargados como referencias contienen formatos o fuentes necesarios para el cumplimiento"
      };
    }

    return {
      response: "You can review this obligation's annexes for more information. Remember to review the official documentation; the files uploaded as references contain templates or sources required for compliance."
    };
  }

  const ENDPOINT = `/api/obligation/obligationai/`;
  const response = await fetch(ENDPOINT, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload)
  });


  if (!response.ok) {
    let detail = "Failed to fetch IA obligation answer";
    try {
      const errorData = await response.json();
      detail = errorData?.detail || errorData?.message || detail;
    } catch {
      try {
        const text = await response.text();
        if (text) detail = text;
      } catch {
        // Keep default detail when response body can't be parsed.
      }
    }
    throw new Error(detail);
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return await response.json();
  }

  const text = await response.text();
  return { response: text };
};
